import { useState } from "react";
import { Employee, PlanReference } from "../../models";
import { useData, useFeatures } from "../../utils/useData";
import { SelectPlan } from "./SelectPlan";
import { useBulkAssignAccounts } from "../../hooks/employeeAccounts/useBulkAssignAccounts";
import { useAssignAccounts } from "../../hooks/employeeAccounts/useAssignAccounts";
import { Dialog, IconButton } from "@mui/material";
import { GiftingCheckout } from "../checkout/Gifting/GiftingCheckout";
import CloseIcon from '@mui/icons-material/Close';
import { EmployerModuleType } from "../../models/accounts/employerModuleType";
import { AllowanceCheckout } from "../checkout/Allowance/AllowanceCheckout";
import { LoadingSpinner } from "../shared/LoadingSpinner";
import { RewardsCheckout } from "../checkout/Rewards/RewardsCheckout";
import { PublicTransportCheckout } from "../checkout/PublicTransport/PublicTransportCheckout";

type AssignToPlanProps = {
  handleClose: () => void;
  selectedEmployees: Employee[];
  shouldhideBackButton?: boolean;
};

const ModuleFormDictionary: Record<string, React.FC<any>> = {
  [EmployerModuleType.ALLOWANCES]: AllowanceCheckout,
  [EmployerModuleType.GIFTING]: GiftingCheckout,
  [EmployerModuleType.REWARDS_RECOGNITION]: RewardsCheckout,
  [EmployerModuleType.PUBLIC_TRANSPORT]: PublicTransportCheckout
}

export const AssignPlans = ({
                              handleClose,
                              selectedEmployees,
                              shouldhideBackButton = false
                            }: AssignToPlanProps) => {
  const { employerId } = useData();
  const { isLoading: isFeaturesLoading, data: features } = useFeatures();
  const [error, setError] = useState("");
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanReference | undefined>();

  const assignSynchronously = useAssignAccounts(employerId, {
    onSuccess: () => handleClose(),
    onError: (error: Error) => setError(error.message)
  });

  const assignAsynchronously = useBulkAssignAccounts(employerId, {
    onSuccess: () => handleClose(),
    onBulkJobComplete: () => handleClose()
  });

  function handleSubmit(selectedPlan?: PlanReference) {
    if(!selectedPlan) return;

    if (selectedEmployees.length === 0) {
      return;
    } else if (selectedEmployees.length <= 10) {
      assignSynchronously.mutate({selectedPlan, selectedEmployees});
    } else {
      assignAsynchronously.mutate({ selectedPlan, targetEmployeeIds: selectedEmployees.map((x) => x.id) });
    }
  }

  var checkoutEnabled = features?.find((f) => f.name === "f_customer_portal_checkouts" && f.isEnabled)


  if(isFeaturesLoading) return <LoadingSpinner />;

  const Checkout = ModuleFormDictionary[selectedPlan?.employerModuleOptions.employerModuleType ?? EmployerModuleType.ALLOWANCES];

  return (
    <>
      <Dialog 
        open={isCheckoutOpen} 
        onClose={() => setIsCheckoutOpen(false)} 
        fullScreen
      >
        <IconButton
          aria-label="close"
          onClick={() => setIsCheckoutOpen(false)}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
            zIndex: 1
          }}
        >
          <CloseIcon />
        </IconButton>
        <Checkout onConfirm={() => handleSubmit(selectedPlan)} selectedPlan={selectedPlan!} selectedEmployees={selectedEmployees} />
      </Dialog>
      <SelectPlan
        handleClose={handleClose}
        selectedEmployees={selectedEmployees}
        onSelectPlan={(selectedPlan) => {
          if(checkoutEnabled) {
            setIsCheckoutOpen(true); 
            setSelectedPlan(selectedPlan)
          } else {
            handleSubmit(selectedPlan)
          }
        }}
        isLoading={assignSynchronously.isLoading || assignAsynchronously.isLoading}
        apiError={error}
        shouldHideBackButton={shouldhideBackButton}
      />
    </>
  );
};
